import { DecimalPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { NbDialogService } from '@nebular/theme';
import { Store } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { Content } from 'src/app/models/content';
import { ACTIONS } from 'src/app/shared/constants/general-constants';
import { SharedModule } from 'src/app/shared/shared.module';
import { NgxGenericModalComponent } from 'src/app/shared/ui/ngx-generic-modal/ngx-generic-modal.component';
import { ENVIRONMENT } from 'src/environments/environment';
import { DrbState, LoadDrbByVersion, LoadDrbVersions } from '../../drbs/redux';
import { ContentState, DeleteContent, LoadContent, ReloadContent } from '../redux';
import { EditContentComponent } from './edit-content/edit-content.component';
import { ViewContentComponent } from './view-content/view-content.component';

@Component({
  selector: 'app-content',
  templateUrl: './content.component.html',
  styleUrls: ['./content.component.css'],
  providers: [DecimalPipe],
  imports: [SharedModule],
  standalone: true
})
export class ContentComponent implements OnInit, OnDestroy {
  public version$: Observable<any> = inject(Store).select(DrbState.versions);
  public content$: Observable<any[]> = inject(Store).select(ContentState.content);
  private unsubscribe = new Subject<void>();

  public tableData;
  public showActions = [ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.CLONE, ACTIONS.DELETE];

  constructor(
    private store: Store,
    private dialogService: NbDialogService
  ) {
    if (!this.store.selectSnapshot(ContentState.content)) {
      this.store.dispatch(LoadContent);
    }
    ENVIRONMENT.bundleNames.forEach((bundle_name) => {
      if (this.store.selectSnapshot(DrbState.versions)[bundle_name].length === 0) {
        this.store.dispatch(new LoadDrbVersions(bundle_name));
      }
    });
  }

  public ngOnInit() {
    this.content$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.tableData = data));
    this.version$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
      ENVIRONMENT.bundleNames.forEach((bundle_name) => {
        if (data[bundle_name].length > 0) {
          const versions = [...data[bundle_name]].reverse();
          if (!this.store.selectSnapshot(DrbState.getByKey(bundle_name)).version) {
            this.store.dispatch(new LoadDrbByVersion(versions[0], bundle_name));
          }
        }
      });
    });
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public clickedButton($event) {
    if ($event.action === ACTIONS.REFRESH) {
      this.tableData = undefined;
      this.store.dispatch(ReloadContent);
    }
    if ($event.action === ACTIONS.CREATE) {
      this.dialogService.open(EditContentComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          action: $event.action,
          content: new Content()
        }
      });
    }
    if ($event.action === ACTIONS.VIEW) {
      const content: Content = $event.data;
      this.dialogService.open(ViewContentComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          content
        }
      });
    }
    if ($event.action === ACTIONS.EDIT) {
      const content: Content = $event.data;
      this.dialogService.open(EditContentComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          action: $event.action,
          content: Content.copy(content) as Content
        }
      });
    }
    if ($event.action === ACTIONS.CLONE) {
      const content: Content = $event.data;
      this.dialogService.open(EditContentComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          action: $event.action,
          content: Content.clone(content)
        }
      });
    }
    if ($event.action === ACTIONS.DELETE) {
      const content: Content = $event.data;
      this.dialogService
        .open(NgxGenericModalComponent, {
          closeOnEsc: false,
          closeOnBackdropClick: false,
          context: {
            title: $localize`Are you sure you want to delete Content`,
            message: content.content_id + ' - ' + content.headline_text
          }
        })
        .onClose.subscribe((data) => {
          if (data === true) {
            const contentToDelete = Content.copy(content);
            contentToDelete.deleted = true;
            this.store.dispatch(new DeleteContent(contentToDelete));
          }
        });
    }
  }
}
